// Ciclos montados a partir do calendário (`find/calendar_v2`), que já traz em cada fase o documento
// do ciclo e do trimestre. Reproduzem, campo a campo, a saída dos aggregates `get_cycles` e
// `get_cycles_extended`, que a sessão deixou de chamar (DCE-59..62).

import { CyclesInterface, RealCycle, Step } from 'types/user';

type Phase = CyclesInterface;
type Fields = Record<string, unknown>;

/** Fases a partir de 2S2024 ganham os passos de semestre e trimestre em `get_cycles_extended`. */
const EXTENDED_FORMAT_SINCE = 1730084400000;
/** Ano de início mínimo dos ciclos que a sessão guarda (`get_cycles_extended` já cortava aqui). */
export const MIN_CYCLE_YEAR = 2023;

const EVALUATION_WINDOW_FIELDS = [
  'evaluation_ecoa_days_step2_start',
  'evaluation_ecoa_days_step2_finish',
  'date_evaluation_step2_start',
  'date_evaluation_step2_finish',
  'date_evaluation_step3_start',
  'date_evaluation_step3_finish',
];

const EVALUATION_DATE_FIELDS = [
  ...EVALUATION_WINDOW_FIELDS,
  'date_evaluation_step4_start',
  'date_evaluation_step4_finish',
  'date_evaluation_pitStop_start',
  'date_evaluation_pitStop_finish',
  'date_evaluation_step5_start',
  'date_evaluation_step5_finish',
];

const STAGE_SHARED_FIELDS = [...EVALUATION_DATE_FIELDS, 'cycleId', 'trimesterId'];

const STEP_FIELDS = [
  '_id',
  'title',
  'date_start',
  'date_finish',
  'date_evaluation',
  'status',
  ...EVALUATION_DATE_FIELDS,
  'trimester_title',
  'semester_title',
  'date_trimester_start',
  'date_trimester_finish',
];

const EXTENDED_STEP_FIELDS = [...STEP_FIELDS, 'date_cycle_start', 'date_cycle_finish', 'cycleId', 'trimesterId'];

const CYCLE_FIELDS = [
  '_id',
  'title',
  'date_start',
  'date_end',
  'date_evaluation_start',
  'date_evaluation_end',
  'title_semester',
  'status',
];

/** Como o `$project` do Mongo: campo ausente ou nulo não aparece na saída. */
function pickDefined(source: Fields, fields: string[]): Fields {
  const picked: Fields = {};
  fields.forEach(field => {
    if (source[field] !== undefined && source[field] !== null) picked[field] = source[field];
  });
  return picked;
}

const byStartDesc = (a: { date_start: number }, b: { date_start: number }) => b.date_start - a.date_start;

const yearOf = (timestamp: number) => new Date(timestamp).getUTCFullYear();

function groupPhasesByCycle(phases: Phase[]) {
  const groups = new Map<string, { cycle: Phase['cycle']; phases: Phase[] }>();

  phases.forEach(phase => {
    if (!phase.cycle || phase.cycle.status === 'waiting') return;
    const group = groups.get(phase.cycle._id) ?? { cycle: phase.cycle, phases: [] };
    group.phases.push(phase);
    groups.set(phase.cycle._id, group);
  });

  return Array.from(groups.values())
    .sort((a, b) => byStartDesc(a.cycle, b.cycle))
    .map(group => ({ cycle: group.cycle, phases: [...group.phases].sort(byStartDesc) }));
}

function cycleHeader(cycle: Phase['cycle']) {
  return { ...pickDefined(cycle as Fields, CYCLE_FIELDS), year: yearOf(cycle.date_start) };
}

function phaseFields(phase: Phase): Fields {
  return { ...phase, trimester_title: phase.trimester?.title, semester_title: phase.cycle?.title_semester };
}

function toStep(phase: Phase): Fields {
  return pickDefined(phaseFields(phase), STEP_FIELDS);
}

function toExtendedStep(phase: Phase): Fields {
  return pickDefined(
    { ...phaseFields(phase), date_cycle_start: phase.cycle.date_start, date_cycle_finish: phase.cycle.date_end },
    EXTENDED_STEP_FIELDS,
  );
}

const isBetween = (now: number, start: unknown, finish: unknown) => now >= (start as number) && now <= (finish as number);

/** Primeiro mês da faixa que está em avaliação agora: empresta a janela de avaliação ao passo maior. */
function monthInEvaluationWithin(steps: Fields[], now: number, start: unknown, finish: unknown) {
  return steps.find(
    step =>
      isBetween(now, step.date_evaluation_step2_start, step.date_evaluation_step3_finish) &&
      (step.date_start as number) >= (start as number) &&
      (step.date_finish as number) <= (finish as number),
  );
}

/** Campos de data do passo maior; com um mês em avaliação, a janela inteira passa a ser a dele, inclusive o que faltar. */
function stageDates(step: Fields, monthInEvaluation: Fields | undefined): Fields {
  if (!monthInEvaluation) return pickDefined(step, STAGE_SHARED_FIELDS);

  const ownFields = STAGE_SHARED_FIELDS.filter(field => !EVALUATION_WINDOW_FIELDS.includes(field));
  return { ...pickDefined(step, ownFields), ...pickDefined(monthInEvaluation, EVALUATION_WINDOW_FIELDS) };
}

/** Onde cada passo maior lê, na fase, a sua faixa de datas e a sua janela de avaliação. */
interface RangeStageFields {
  type: 'semester' | 'trimester';
  step: number;
  id: string;
  title: string;
  start: string;
  finish: string;
  evaluationStart: string;
  evaluationFinish: string;
}

const SEMESTER_STAGE: RangeStageFields = {
  type: 'semester',
  step: 5,
  id: 'cycleId',
  title: 'semester_title',
  start: 'date_cycle_start',
  finish: 'date_cycle_finish',
  evaluationStart: 'date_evaluation_step5_start',
  evaluationFinish: 'date_evaluation_step5_finish',
};

const TRIMESTER_STAGE: RangeStageFields = {
  type: 'trimester',
  step: 4,
  id: 'trimesterId',
  title: 'trimester_title',
  start: 'date_trimester_start',
  finish: 'date_trimester_finish',
  evaluationStart: 'date_evaluation_step4_start',
  evaluationFinish: 'date_evaluation_pitStop_finish',
};

function rangeStage(step: Fields, steps: Fields[], now: number, fields: RangeStageFields): Fields {
  const start = step[fields.start];
  const finish = step[fields.finish];
  const status = isBetween(now, step[fields.evaluationStart], step[fields.evaluationFinish])
    ? 'evaluation'
    : isBetween(now, start, finish)
      ? 'open'
      : 'closed';

  return {
    _id: step[fields.id],
    title: step[fields.title],
    date_start: start,
    date_finish: finish,
    status,
    type: fields.type,
    step: fields.step,
    ...stageDates(step, monthInEvaluationWithin(steps, now, start, finish)),
  };
}

function monthStage(step: Fields, now: number): Fields {
  const outsideMonth = now < (step.date_start as number) || now > (step.date_evaluation_step3_finish as number);

  return {
    _id: step._id,
    title: step.title,
    date_start: step.date_start,
    date_finish: step.date_finish,
    status: outsideMonth ? 'closed' : step.status,
    type: 'month',
    ...pickDefined(step, STAGE_SHARED_FIELDS),
  };
}

/** Passos de um ciclo como o `$function` de `get_cycles_extended` os monta. */
function extendedStages(steps: Fields[], now: number): Fields[] {
  const stages: Fields[] = [];
  const hasStage = (id: unknown) => stages.some(stage => stage._id === id);

  steps.forEach(step => {
    // Negação de `>=` de propósito: fase sem `date_start` também fica no formato antigo, como no aggregate.
    if (!((step.date_start as number) >= EXTENDED_FORMAT_SINCE)) {
      stages.push(step);
      return;
    }
    if (!hasStage(step.cycleId)) stages.push(rangeStage(step, steps, now, SEMESTER_STAGE));
    if (!hasStage(step.trimesterId)) stages.push(rangeStage(step, steps, now, TRIMESTER_STAGE));
    stages.push(monthStage(step, now));
  });

  return stages;
}

/** Mesma saída de `find/get_cycles`. */
export function toRealCycles(phases: Phase[]): RealCycle[] {
  return groupPhasesByCycle(phases).map(
    ({ cycle, phases: cyclePhases }) => ({ ...cycleHeader(cycle), steps: cyclePhases.map(toStep) }) as unknown as RealCycle,
  );
}

/** Mesma saída de `find/get_cycles_extended` no instante `now`. */
export function toCyclesExtended(phases: Phase[], now: number): RealCycle[] {
  return groupPhasesByCycle(phases)
    .filter(({ cycle }) => yearOf(cycle.date_start) >= MIN_CYCLE_YEAR)
    .map(
      ({ cycle, phases: cyclePhases }) =>
        ({
          ...cycleHeader(cycle),
          steps: extendedStages(cyclePhases.map(toExtendedStep), now) as unknown as Step[],
        }) as RealCycle,
    );
}
